import { format, parseISO } from 'date-fns';
import type { KgString } from './kg';

/**
 * Format a kg decimal string for the UI.
 * Default: 3 decimal places (matches the Master Spec promise of
 * "42403.000"-style values from 06-ui-api §4) with thousands separators.
 */
export function formatKg(
  value: KgString | null | undefined,
  options: { fractionDigits?: number; locale?: string } = {},
): string {
  if (value == null || value === '') return '-';
  const { fractionDigits = 3, locale = 'ja-JP' } = options;
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

/** Format an ISO 8601 timestamp as date+time for tables. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    return format(parseISO(iso), 'yyyy-MM-dd HH:mm');
  } catch {
    return iso;
  }
}

/** Format a YYYY-MM-DD business date for display. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  try {
    return format(parseISO(value), 'yyyy-MM-dd');
  } catch {
    return value;
  }
}

/** Format a monthly batch key like "2026-04" -> "2026/04". */
export function formatBatchMonth(value: string | null | undefined): string {
  if (!value) return '-';
  return value.replace('-', '/');
}

export interface BatchFacilityFields {
  facility_id?: string | null;
  facility_name?: string | null;
  company_display_name?: string | null;
  facility_role?: string | null;
}

/** Human-readable facility label for batch lists and selectors. */
export function formatBatchFacility(value: BatchFacilityFields): string {
  const facility = value.facility_name?.trim() || value.facility_id?.slice(0, 8) || '-';
  const company = value.company_display_name?.trim();
  return company ? `${facility} (${company})` : facility;
}
